// Chat de teste: conversas com streaming SSE e imagens (/api/client/chats*).
// Também serve de conversa com um Agente (agentId): mesma UI, lista e system prompt do agente.
import { memo, useCallback, useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, brl, rateFor, readSSE, type Agent, type Doc, type Me, type Message, type Source, type Thread, type ThreadDetail } from '../api';
import { useMeRequired } from '../me';
import { fmtMult, isSpecificScope, scopeName, useScope, useScopeModels } from '../scope';
import { modelInScope } from '../models/catalog';
import { LABELS } from '../labels';
import { IconClip, IconPlus, IconSend } from '../components/Icons';
import { AgentAvatar } from '../components/agentMeta';
import { ModelPicker } from '../components/ModelPicker';
import { DocPicker } from '../components/DocPicker';
import { DocViewer, type ViewerTarget } from '../components/DocViewer';
import { DOC_ACCEPT, isDocFile, stashPick, takePick, uploadDocument } from '../docUpload';
import { ChatQueue } from '../components/ChatQueue';
import { CopyButton } from '../components/CopyButton';
import { MessageBody } from '../components/Markdown';
import { accepted, clear, closing, createMachine, QUEUE_MAX, remove, resume, reset, sendNow, settle, stop, submit as submitItem, type Effect, type Item, type Machine, type Phase, type Run } from '../chatQueue';
import { ArrowLeft, Check, FileText, Pencil, Trash2, X } from 'lucide-react';
import { toast } from '../components/toast';
import '../styles/chat.css';

type Meta = { kind: 'text'; text: string } | { kind: 'queued'; position: number } | { kind: 'started'; node: string; tokens: number; cost: number; secs: number; jobId: string | null; attached?: boolean } | { kind: 'done'; text: string };
// `id` identifica a bolha entre renders (a fila despacha vários envios em sequência: quem atualiza uma bolha a acha pelo id, nunca por "a última")
type Bubble = { id: number; role: 'user' | 'assistant'; text: string; images: string[]; err?: boolean; cursor?: boolean; interrupted?: boolean; meta?: Meta; sources?: Source[]; note?: string };

const MAX_DOCS = 5;   // documentos por conversa (o servidor recusa mais que isso)
const NOTICE = 'Trechos dos seus documentos serão processados em máquinas de terceiros da rede (ou por um provedor externo). Não envie dados sensíveis.';
const NO_SOURCES = 'Nenhum trecho dos documentos parece relevante; respondi só com o que sei';

async function fileToJpegBase64(file: File): Promise<string | null> {
  if (!file.type.startsWith('image/')) return null;
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, 1024 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas'); c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
  c.getContext('2d')?.drawImage(bmp, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.85).split(',')[1] ?? null;
}

/** Estado vivo de UMA resposta que a tela acompanha (o envio e a re-anexação usam o mesmo). */
type Live = { patchBubble: (fn: (b: Bubble) => Bubble) => void; rate: { chargeOut: number }; jobId: string | null; t0: number; tokens: number; acc: string };

/**
 * Eventos de uma tarefa (`queued` · `started` · `snapshot` · `delta` · `done` · `error`) → bolha. É o tratamento ÚNICO das duas origens: o SSE do envio
 * (POST /messages) e o da re-anexação a uma resposta em andamento (GET /chats/:id/jobs/:jobId/stream, que abre com um `snapshot` do texto acumulado).
 * Só mexe na bolha; fila, saldo e `accepted` ficam com quem chama. `asked`: o erro veio de um "parar"/"enviar agora" do próprio usuário.
 */
function applyJobEvent(live: Live, attached: boolean, event: string, d: Record<string, unknown>, asked = false) {
  const { patchBubble } = live;
  const started = (node: string): Meta => ({ kind: 'started', node, tokens: live.tokens, cost: live.tokens / 1e6 * live.rate.chargeOut, secs: (Date.now() - live.t0) / 1000, jobId: live.jobId, ...(attached ? { attached: true } : {}) });
  switch (event) {
    case 'queued': patchBubble((b) => ({ ...b, meta: { kind: 'queued', position: Number(d.position) } })); break;
    case 'started': live.t0 = Date.now(); live.acc = ''; live.tokens = 0; patchBubble((b) => ({ ...b, text: '', cursor: true, meta: started(String(d.node ?? '')) })); break;
    case 'snapshot': live.acc = String(d.text ?? ''); live.tokens = Math.ceil(live.acc.length / 4); live.t0 = Date.now();   // o que já foi escrito antes de a tela voltar (tokens estimados: ~4 caracteres)
      patchBubble((b) => ({ ...b, text: live.acc, cursor: true, meta: started(b.meta?.kind === 'started' ? b.meta.node : '') })); break;
    case 'delta': live.acc += String(d.text ?? ''); live.tokens++; {
      const cost = live.tokens / 1e6 * live.rate.chargeOut, secs = (Date.now() - live.t0) / 1000;
      patchBubble((b) => ({ ...b, text: live.acc, meta: b.meta?.kind === 'started' ? { ...b.meta, tokens: live.tokens, cost, secs } : b.meta }));
    } break;
    case 'done': {
      const interrupted = /interrompid/i.test(String(d.note ?? ''));   // o servidor marca a resposta cortada por "parar"/"enviar agora"
      const line = `${d.model ?? ''} · ${d.node ?? ''} · ${(Number(d.durationMs) / 1000).toFixed(1)}s · ${d.inputTokens} in / ${d.outputTokens} out · ${brl(d.charge)}${d.note && !interrupted ? ` · ${d.note}` : ''}`;
      patchBubble((b) => ({ ...b, text: String(d.output ?? ''), cursor: false, interrupted, meta: { kind: 'done', text: line } }));
      break;
    }
    case 'error': {
      const msg = String(d.error ?? '');
      patchBubble((b) => (asked ? { ...b, cursor: false, interrupted: true, meta: undefined } : { ...b, text: msg, err: true, cursor: false, meta: { kind: 'text', text: 'não concluída' } }));
      break;
    }
  }
}

export function Chat({ agentId }: { agentId?: string } = {}) {
  const { me, patch } = useMeRequired();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const base = agentId ? `/agentes/${agentId}` : '/chat';
  const listUrl = `/api/client/chats${agentId ? `?agentId=${agentId}` : ''}`;
  const [agent, setAgent] = useState<Agent | null>(null);
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [thread, setThread] = useState<ThreadDetail | null>(null);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [model, setModel] = useState('');
  const [input, setInput] = useState('');
  const [pending, setPending] = useState<string[]>([]);
  // envio e fila: a máquina (chatQueue.ts) mora num ref, para decidir sempre sobre o estado mais recente; `view` só espelha para a tela
  const mach = useRef<Machine>(createMachine());
  const [view, setView] = useState<{ phase: Phase; queue: Item[] }>({ phase: 'idle', queue: [] });
  const [announce, setAnnounce] = useState('');      // anúncio (aria-live) de entrada/saída de itens da fila
  const streaming = view.phase !== 'idle';
  const chatRef = useRef<{ id: string } | null>(null);   // conversa aberta (ainda não existe numa conversa nova até a 1ª mensagem)
  const convGen = useRef(0);                              // sobe a cada troca de conversa: respostas atrasadas da anterior não mexem na tela
  const ctrlRef = useRef<AbortController | null>(null);
  const execRef = useRef<(run: Run) => Promise<void>>(async () => {});
  const bubbleSeq = useRef(0);
  const itemSeq = useRef(0);
  const meRef = useRef(me); meRef.current = me;
  // escopo (onde o chat processa) e os modelos dele: o seletor, a tarifa e o POST leem daqui; o Modo Desenvolvedor pede também o que está instalado (`detail`)
  const { active: scope, current: scopeRow, reload: reloadScopes } = useScope();
  const sm = useScopeModels(scope, me.prefs?.developer === true && isSpecificScope(scopeRow));
  const scopeMult = scopeRow?.multiplier ?? 1;   // multiplicador de preço do escopo: o valor cobrado já o inclui
  const rateOf = (m: string) => {   // tarifa por 1M tokens × multiplicador do escopo (comercial fica de fora: a OpenRouter tem preço próprio e não roda em pool dedicada/on-premise)
    const r = rateFor(meRef.current, m || sm.data?.models[0]?.id || meRef.current.models[0]), k = m.startsWith('openrouter/') ? 1 : scopeMult;
    return { chargeIn: r.chargeIn * k, chargeOut: r.chargeOut * k };
  };
  // trocou o escopo nesta sessão e o modelo escolhido não existe no novo: volta para o Izze e avisa (uma conversa aberta com um modelo que já não existe segue como "indisponível")
  const lastScope = useRef(scope), recheck = useRef(false);
  useEffect(() => { if (lastScope.current !== scope) { lastScope.current = scope; recheck.current = true; } }, [scope]);
  useEffect(() => {
    const d = sm.data;
    if (!recheck.current || !d || d.scope !== scope) return;
    recheck.current = false;
    if (!modelInScope(model, d)) { setModel(''); toast(`Modelo trocado para ${LABELS.izze}: não existe em ${scopeRow ? scopeName(scopeRow) : scope}`); }
  }, [sm.data, scope, model, scopeRow]);
  const [picked, setPicked] = useState<Doc[]>([]);   // documentos escolhidos para a conversa aberta (não persiste ao recarregar)
  const [notice, setNotice] = useState(false);       // confirmação de privacidade aberta (Q13)
  const msgsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // renomear / apagar: `editing` diz onde o campo aparece (lista ou cabeçalho); `confirming` é a confirmação inline de apagar
  const [editing, setEditing] = useState<{ id: string; where: 'list' | 'head' } | null>(null);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [delItems, setDelItems] = useState<number | null>(null);   // quantos itens da biblioteca nasceram na conversa que está para ser apagada (saem junto)

  const scrollDown = () => { const b = msgsRef.current; if (b) b.scrollTop = b.scrollHeight; };

  // agente (se houver): nome/ícone/cor pro cabeçalho e pra vazio da lista
  useEffect(() => {
    if (!agentId) { setAgent(null); return; }
    let alive = true;
    api<Agent>(`/api/client/agents/${agentId}`).then((a) => { if (alive) setAgent(a); }).catch(() => { if (alive) nav('/agentes', { replace: true }); });
    return () => { alive = false; };
  }, [agentId, nav]);

  // lista de conversas (do agente, se houver); sem id, abre a mais recente.
  // Só redireciona se a URL ainda é a deste chat: na animação de saída do Shell esta instância segue montada por uns
  // ms já com os params da rota nova (sem agentId), virava "chat comum" e jogava o usuário em /chat/<última> ao sair do agente.
  useEffect(() => {
    let alive = true;
    setThreads(null);
    const stillHere = () => window.location.pathname.replace(/\/+$/, '') === base;
    api<Thread[]>(listUrl).then((t) => { if (!alive) return; setThreads(t); if (!id && t[0] && stillHere()) nav(`${base}/${t[0].id}`, { replace: true }); }).catch(() => { if (alive) setThreads([]); });
    return () => { alive = false; };
  }, [listUrl]);

  // ---- re-anexar a uma resposta em andamento (só visualização: não passa pela fila de chatQueue.ts e não bloqueia novos envios)
  const attached = useRef(new Map<string, AbortController>());   // jobId → stream aberto (nunca dois para a mesma resposta)
  const reattach = (bid: number, jobId: string, chatId: string, modelId: string) => {
    if (attached.current.has(jobId)) return;
    const ctrl = new AbortController(); attached.current.set(jobId, ctrl);
    const gen = convGen.current;
    const patchBubble = (fn: (b: Bubble) => Bubble) => setBubbles((cur) => cur.map((b) => (b.id === bid ? fn(b) : b)));
    const live: Live = { patchBubble, rate: rateOf(modelId), jobId, t0: Date.now(), tokens: 0, acc: '' };
    void (async () => {
      let terminal = false;
      try {
        const r = await fetch(`/api/client/chats/${chatId}/jobs/${jobId}/stream`, { signal: ctrl.signal });
        if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error((e as { error?: string }).error ?? `HTTP ${r.status}`); }
        await readSSE(r, (event, d) => {
          if (convGen.current !== gen) return;
          if (event === 'accepted') { live.jobId = String(d.jobId ?? jobId); return; }
          if (event === 'done' || event === 'error') terminal = true;
          if (event === 'done' && d.balance != null) patch({ credits: Number(d.balance) });
          applyJobEvent(live, true, event, d);
          if (event === 'delta') scrollDown();
        });
        if (!terminal && !ctrl.signal.aborted) patchBubble((b) => ({ ...b, cursor: false, meta: { kind: 'text', text: 'conexão encerrada antes do fim — abra a conversa de novo para ver o resultado' } }));
      } catch (err) {
        if (!ctrl.signal.aborted) patchBubble((b) => ({ ...b, text: (err as Error).message, err: true, cursor: false, meta: { kind: 'text', text: 'não foi possível retomar' } }));
      } finally { if (attached.current.get(jobId) === ctrl) attached.current.delete(jobId); }
    })();
  };
  const [viewer, setViewer] = useState<ViewerTarget | null>(null);   // fonte aberta na pré-visualização
  const openSource = useCallback((s: Source, all: Source[]) => setViewer({ documentId: s.documentId, name: s.name, cited: all.filter((x) => x.documentId === s.documentId && x.seq != null).map((x) => x.seq as number), focus: s.seq ?? null, tab: 'text' }), []);
  const onStopJob = useCallback((jobId: string) => { void fetch(`/v1/jobs/${jobId}/cancel`, { method: 'POST' }).catch(() => {}); }, []);   // a bolha re-anexada para a tarefa direto (a máquina de fila não a conhece)

  // conversa aberta
  useEffect(() => {
    if (!id) { chatRef.current = null; setThread(null); setBubbles([]); return; }
    let alive = true;
    api<ThreadDetail>(`/api/client/chats/${id}`).then((t) => {
      if (!alive) return;
      chatRef.current = { id: t.id };
      const saved = t.model ?? '', turboOff = saved.startsWith('band:') && !meRef.current.prefs?.turbo;   // Modo Turbo desligado com a conversa numa faixa: volta para o Izze
      setThread(t); setModel(turboOff ? '' : saved);
      if (turboOff) toast(`Modo Turbo desligado: voltei para o ${LABELS.izze}`);
      // `[erro: …]` é como o servidor grava uma resposta que falhou: aparece como erro (e sem "Copiar"), igual a quando acabou de acontecer
      setBubbles(t.messages.map((m: Message) => ({ id: ++bubbleSeq.current, role: m.role, text: m.content, images: m.images ?? [], err: m.role === 'assistant' && /^\[erro: /.test(m.content) ? true : undefined, sources: m.sources?.length ? m.sources : undefined, meta: m.role === 'assistant' && !m.content ? { kind: 'text', text: 'retomando…' } : undefined })));
      // resposta ainda em andamento no servidor (a tela saiu e voltou): re-anexa às últimas 3 e acompanha ao vivo; as mais antigas ficam sem acompanhamento
      const pend = t.messages.map((m: Message, i: number) => ({ m, i })).filter(({ m }) => m.role === 'assistant' && !m.content && m.job_id);
      pend.forEach(({ m, i }, k) => {
        const bid = bubbleSeq.current - t.messages.length + 1 + i;   // o id da bolha criada acima para a mensagem i
        if (k >= pend.length - 3) reattach(bid, String(m.job_id), t.id, t.model ?? '');
        else setBubbles((cur) => cur.map((b) => (b.id === bid ? { ...b, meta: { kind: 'text', text: 'resposta em andamento (não acompanhada: abra a conversa de novo mais tarde)' } } : b)));
      });
      setTimeout(() => { scrollDown(); inputRef.current?.focus(); }, 0);
    }).catch(() => { if (alive) nav(base, { replace: true }); });
    return () => { alive = false; };
  }, [id, nav, base]);

  useEffect(() => { setPicked(takePick()); setNotice(false); }, [id]);   // a seleção vale para a conversa aberta até ela mudar (um documento recém-anexado, que criou a conversa, é retomado aqui)

  // a fila e o envio em curso valem para a conversa aberta: ao trocar de conversa, trocar de agente ou sair da página, somem
  // (o job que já estava no servidor termina e fica gravado; só a tela deixa de acompanhá-lo)
  useEffect(() => () => {
    convGen.current++;
    ctrlRef.current?.abort(); ctrlRef.current = null;
    for (const c of attached.current.values()) c.abort();   // a tarefa continua no servidor; só a tela deixa de acompanhar
    attached.current.clear();
    reset(mach.current);
    setView({ phase: 'idle', queue: [] });
  }, [id, agentId]);
  // fechar ou recarregar com itens na fila perderia o que a pessoa escreveu: o navegador pergunta
  useEffect(() => {
    if (!view.queue.length) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [view.queue.length]);

  const addImage = useCallback(async (file: File | null) => {
    if (!file) return;
    const b64 = await fileToJpegBase64(file);
    if (!b64) return;
    setPending((cur) => (cur.length >= 4 ? cur : [...cur, b64]));
  }, []);

  // anexar documento (texto/PDF): cria o documento com origem NESTA conversa (F16) e o seleciona (F03). Sem conversa aberta, cria uma e navega para ela
  const attachDoc = useCallback(async (file: File) => {
    try {
      let cid = chatRef.current?.id ?? id;
      if (!cid) { const t = await api<Thread>('/api/client/chats', { method: 'POST', body: JSON.stringify({ model: model || null, agentId: agentId ?? null }) }); cid = t.id; setThreads((cur) => [t, ...(cur ?? [])]); }
      const d = await uploadDocument(file, cid);
      if (!id) { stashPick(d); nav(`${base}/${cid}`); } else setPicked((cur) => (cur.length >= MAX_DOCS || cur.some((x) => x.id === d.id) ? cur : [...cur, d]));
      toast('Documento anexado à conversa');
    } catch (e) { toast((e as Error).message, 'err'); }
  }, [id, model, agentId, base, nav]);
  const attach = useCallback(async (f: File) => { if (isDocFile(f)) await attachDoc(f); else await addImage(f); }, [attachDoc, addImage]);

  // arrastar e soltar imagens e documentos em qualquer lugar da página
  useEffect(() => {
    const over = (e: DragEvent) => e.preventDefault();
    const drop = async (e: DragEvent) => { e.preventDefault(); for (const f of e.dataTransfer?.files ?? []) await attach(f); };
    document.addEventListener('dragover', over); document.addEventListener('drop', drop);
    return () => { document.removeEventListener('dragover', over); document.removeEventListener('drop', drop); };
  }, [attach]);

  const onPaste = async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    for (const it of e.clipboardData?.items ?? []) if (it.type.startsWith('image/')) { e.preventDefault(); await addImage(it.getAsFile()); }
  };

  const newThread = async () => {
    const t = await api<Thread>('/api/client/chats', { method: 'POST', body: JSON.stringify({ model: model || null, agentId: agentId ?? null }) });
    setThreads((cur) => [t, ...(cur ?? [])]);
    nav(`${base}/${t.id}`);
  };

  const askDelete = (tid: string) => {
    setConfirming(tid); setDelItems(null);
    api<unknown[]>(`/api/client/library?chatId=${encodeURIComponent(tid)}`).then((l) => setDelItems(l.length)).catch(() => setDelItems(null));
  };
  const delWarn = (
    <>
      {delItems ? <>Apagar também remove {delItems} {delItems === 1 ? 'item enviado' : 'itens enviados'} nesta conversa. </> : null}
      {me.org ? 'A empresa mantém o registro desta conversa.' : 'A auditoria da rede mantém o registro do processamento.'}
    </>
  );

  const delThread = async (tid: string) => {
    setConfirming(null);
    try { await api(`/api/client/chats/${tid}`, { method: 'DELETE' }); } catch (e) { toast((e as Error).message, 'err'); return; }
    setThreads((cur) => (cur ?? []).filter((t) => t.id !== tid));
    if (tid === id) { setThread(null); nav(base); }
    toast('Conversa apagada');
  };

  const startRename = (tid: string, current: string | null, where: 'list' | 'head') => { setConfirming(null); setDraft(current ?? ''); setEditing({ id: tid, where }); };
  const cancelRename = () => setEditing(null);
  const commitRename = async () => {
    if (!editing) return;
    const { id: tid } = editing, title = draft.replace(/\s+/g, ' ').trim();
    setEditing(null);
    const cur = (threads ?? []).find((t) => t.id === tid);
    if (!title || title === (cur?.title ?? '')) return;
    try {
      const r = await api<{ title: string }>(`/api/client/chats/${tid}`, { method: 'PATCH', body: JSON.stringify({ title }) });
      setThreads((list) => (list ?? []).map((t) => (t.id === tid ? { ...t, title: r.title } : t)));
      setThread((t) => (t && t.id === tid ? { ...t, title: r.title } : t));
    } catch (e) { toast((e as Error).message, 'err'); }
  };
  const renameKeys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void commitRename(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
  };

  // Q13: com consumo compartilhado/prioritário ou modelo externo, a 1ª vez que trechos de documentos saem pede ciência; nenhum POST antes dela
  // (nem entra na fila: o aviso vem antes de enfileirar)
  const needsNotice = (scopeRow?.kind ?? 'community') === 'community' || model.startsWith('openrouter/');   // a Community roda em máquina de terceiros; pool dedicada e on-premise, não
  const confirmNotice = async () => {
    try { const r = await api<{ prefs: Me['prefs'] }>('/api/client/preferences', { method: 'PATCH', body: JSON.stringify({ docsNoticeAck: true }) }); patch({ prefs: r.prefs }); }
    catch (e) { toast((e as Error).message, 'err'); return; }
    submit(true);
  };
  // documento apagado em outra aba: o servidor responde 404 e os chips que sobraram saem
  const pruneDocs = () => { api<Doc[]>('/api/client/documents').then((list) => setPicked((cur) => cur.filter((d) => list.some((x) => x.id === d.id)))).catch(() => {}); };

  // ---- envio e fila: toda mudança passa por uma transição de chatQueue.ts (síncrona); `commit` espelha na tela e executa os efeitos
  const runEffects = (fx: Effect[]) => {
    for (const e of fx) {
      if (e.kind === 'start') void execRef.current(e.run);
      else void fetch(`/v1/jobs/${e.jobId}/cancel`, { method: 'POST' }).catch(() => {});
    }
  };
  const commit = (fx: Effect[]) => { setView({ phase: mach.current.phase, queue: [...mach.current.queue] }); runEffects(fx); };
  const say = (msg: string) => setAnnounce(msg);
  const backToInput = () => { inputRef.current?.focus(); };

  /** Enter / botão: sem resposta em curso envia na hora; com ela (ou com a fila pausada) entra no fim da fila. */
  const submit = (acked = false) => {
    const text = input.trim();
    if (!text && !pending.length) return;
    if (picked.length && !acked && needsNotice && !me.prefs?.docsNoticeAck) { setNotice(true); return; }
    const item: Item = { key: ++itemSeq.current, text, images: pending, documentIds: picked.map((d) => d.id), model };
    const fx = submitItem(mach.current, item);
    if (!fx) { toast(`fila cheia (máx. ${QUEUE_MAX})`, 'err'); return; }   // o texto fica no campo
    setNotice(false); setPending([]); setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    if (mach.current.queue.includes(item)) say(`Mensagem adicionada à fila. ${mach.current.queue.length} na fila.`);
    commit(fx);
  };
  const onRemove = useCallback((key: number) => { remove(mach.current, key); say('Mensagem removida da fila.'); commit([]); backToInput(); }, []);
  const onSendNow = useCallback((key: number) => { const fx = sendNow(mach.current, key); say('Enviando agora.'); commit(fx); backToInput(); }, []);
  const onResume = useCallback(() => { const fx = resume(mach.current); say('Fila retomada.'); commit(fx); backToInput(); }, []);
  const onClear = useCallback(() => { clear(mach.current); say('Fila limpa.'); commit([]); backToInput(); }, []);
  const onStop = useCallback(() => { const fx = stop(mach.current); if (mach.current.queue.length) say(`Fila pausada. ${mach.current.queue.length} na fila.`); commit(fx); }, []);

  /** Um envio: cria a conversa se preciso, faz o POST e acompanha o SSE até o fim; no fim, a máquina decide o que vem depois. */
  execRef.current = async (run: Run) => {
    const { item } = run, gen = convGen.current, aid = ++bubbleSeq.current;
    const patchBubble = (fn: (b: Bubble) => Bubble) => setBubbles((cur) => cur.map((b) => (b.id === aid ? fn(b) : b)));
    const ctrl = new AbortController(); ctrlRef.current = ctrl;
    let ok = false, terminal = false;
    try {
      let t = chatRef.current;
      if (!t) {
        let created: Thread;
        try { created = await api<Thread>('/api/client/chats', { method: 'POST', body: JSON.stringify({ model: item.model || null, agentId: agentId ?? null }) }); }
        catch (e) {   // nada saiu: o texto volta ao campo (se a pessoa não digitou outra coisa nesse meio-tempo)
          if (convGen.current !== gen) return;
          toast((e as Error).message, 'err');
          setInput((cur) => cur || item.text); setPending((cur) => (cur.length ? cur : item.images));
          commit(settle(mach.current, run, false));
          return;
        }
        if (convGen.current !== gen) return;
        t = { id: created.id }; chatRef.current = t;
        setThread({ ...created, messages: [] }); setThreads((cur) => [created, ...(cur ?? [])]);
        window.history.replaceState({}, '', `${base}/${created.id}`);
      }
      setBubbles((cur) => [...cur,
        { id: ++bubbleSeq.current, role: 'user', text: item.text || 'Descreva esta imagem em detalhes.', images: item.images.map((b) => `data:image/jpeg;base64,${b}`) },
        { id: aid, role: 'assistant', text: '', images: [], meta: { kind: 'text', text: 'enviando' } }]);
      setTimeout(scrollDown, 0);
      const rate = rateOf(item.model);
      let jobId: string | null = null;
      const live: Live = { patchBubble, rate, jobId: null, t0: Date.now(), tokens: 0, acc: '' };
      try {
        const r = await fetch(`/api/client/chats/${t.id}/messages`, { method: 'POST', signal: ctrl.signal, headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text: item.text, images: item.images, model: item.model || null, scope, ...(item.documentIds.length ? { documentIds: item.documentIds } : {}) }) });
        if (!r.ok) {
          if (r.status === 404 && item.documentIds.length) pruneDocs();
          if (r.status === 403 || r.status === 409) void reloadScopes();   // escopo sem acesso (revogado, sem máquinas…): a mensagem do servidor aparece na bolha e o seletor recarrega
          const e = await r.json().catch(() => ({})); throw new Error((e as { error?: string }).error ?? `HTTP ${r.status}`);
        }
        await readSSE(r, (event, d) => {
          switch (event) {
            case 'accepted': jobId = live.jobId = String(d.jobId); runEffects(accepted(mach.current, run, jobId)); break;   // pediu para interromper antes de o job existir? sai agora
            case 'sources': { const docs = (d.documents as { id: string; name: string; chunks: number }[]) ?? []; patchBubble((b) => ({ ...b, sources: docs.map((x) => ({ documentId: x.id, name: x.name, chunks: x.chunks })), note: d.empty ? NO_SOURCES : undefined })); break; }
            case 'queued': case 'started': applyJobEvent(live, false, event, d); break;
            case 'delta': applyJobEvent(live, false, event, d); scrollDown(); break;
            case 'done':
              terminal = ok = true; closing(mach.current, run);
              applyJobEvent(live, false, event, d);
              if (d.balance != null) patch({ credits: Number(d.balance) });
              break;
            case 'error': {
              terminal = true; closing(mach.current, run);
              applyJobEvent(live, false, event, d, run.intent.kind !== 'none' && /interrompid/i.test(String(d.error ?? '')));   // cancelada ainda na fila do servidor: não é falha
              break;
            }
          }
        });
        if (!terminal) patchBubble((b) => ({ ...b, cursor: false, meta: { kind: 'text', text: 'conexão encerrada antes do fim — recarregue para ver o resultado' } }));
      } catch (err) {
        if (ctrl.signal.aborted) return;   // a conversa mudou: esta tela já não acompanha esse envio
        patchBubble((b) => ({ ...b, text: (err as Error).message, err: true, cursor: false, meta: { kind: 'text', text: 'não enviada' } }));
      }
    } finally {
      if (ctrlRef.current === ctrl) ctrlRef.current = null;
    }
    if (convGen.current !== gen) return;
    // a resposta já está gravada e a bolha final (o servidor só manda `done` depois de gravar): só agora a máquina libera o próximo
    commit(settle(mach.current, run, ok));
    // o que sobra não segura a fila e só mexe na bolha deste envio (por id) e na lista de conversas
    if (item.documentIds.length && ok) {   // a lista definitiva das fontes (com o nº do trecho) é a que o servidor guardou
      try { const full = await api<ThreadDetail>(`/api/client/chats/${chatRef.current?.id}`); const last = full.messages.find((m) => m.role === 'assistant' && m.job_id === run.jobId); if (convGen.current === gen && last?.sources?.length) { patchBubble((b) => ({ ...b, sources: last.sources })); setTimeout(scrollDown, 0); } } catch { /* fica a lista do streaming */ }
    }
    try {
      const list = await api<Thread[]>(listUrl); if (convGen.current !== gen) return; setThreads(list);
      const cur = list.find((x) => x.id === chatRef.current?.id); if (cur) setThread((th) => (th ? { ...th, title: cur.title } : th));
    } catch { /* lista fica como está */ }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } };
  const autoGrow = () => { const t = inputRef.current; if (!t) return; t.style.height = 'auto'; t.style.height = Math.min(180, t.scrollHeight) + 'px'; };

  const rateLine = () => {
    if (me.credits <= 0) return 'sem saldo — peça uma recarga ao administrador';
    const r = rateOf(model), or = model.startsWith('openrouter/'), k = or ? 1 : scopeMult;   // tarifa base × multiplicador do escopo = o que é cobrado
    return `saldo ${brl(me.credits, 2)} · tarifa ${brl(r.chargeIn)} / ${brl(r.chargeOut)} por 1M tokens (entrada / saída)${k !== 1 ? ` · ${fmtMult(k)}${scopeRow ? ` ${scopeName(scopeRow)}` : ''}` : ''}${or ? ' · roda na OpenRouter, fora da rede Clusterizze' : ''}`;
  };

  return (
    <div className="chat">
      <div className="threads">
        <div className="threads-h" style={{ padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button className="btn ghost" onClick={(e) => { e.preventDefault(); void newThread(); }} style={{ width: '100%', justifyContent: 'center', fontSize: '13px' }}>
            <IconPlus /> Nova conversa
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {(threads ?? []).map((t) => {
            const isEditing = editing?.id === t.id && editing.where === 'list';
            return (
              <div key={t.id} className="t-wrap">
                {isEditing ? (
                  <div className="t editing">
                    <input className="t-input" autoFocus maxLength={80} value={draft} aria-label="Novo nome da conversa" placeholder="Nome da conversa"
                      onChange={(e) => setDraft(e.target.value)} onKeyDown={renameKeys} onBlur={() => void commitRename()} onFocus={(e) => e.currentTarget.select()} />
                    <span className="date">enter salva · esc cancela</span>
                  </div>
                ) : (
                  <a className={`t ${t.id === id ? 'on' : ''}`} href={`${base}/${t.id}`} title={t.title ?? ''} onClick={(e) => { e.preventDefault(); nav(`${base}/${t.id}`); }}>
                    <span className="title">{t.title ?? 'Nova conversa'}</span>
                    <span className="date">{new Date(t.created_at).toLocaleDateString('pt-BR')}</span>
                  </a>
                )}
                {!isEditing && (confirming === t.id ? (
                  <div className="t-confirm" role="alertdialog" aria-label="Confirmar exclusão">
                    <span title={`${delItems ? `Apagar também remove ${delItems} item(ns) enviado(s) nesta conversa. ` : ''}${me.org ? 'A empresa mantém o registro desta conversa.' : 'A auditoria da rede mantém o registro do processamento.'}`}>Apagar?</span>
                    <button type="button" className="t-yes" onClick={() => void delThread(t.id)} aria-label="Confirmar: apagar conversa"><Check /></button>
                    <button type="button" onClick={() => setConfirming(null)} aria-label="Cancelar"><X /></button>
                  </div>
                ) : (
                  <div className="t-actions">
                    <button type="button" onClick={() => startRename(t.id, t.title, 'list')} aria-label={`Renomear ${t.title ?? 'conversa'}`} title="Renomear"><Pencil /></button>
                    <button type="button" className="danger" onClick={() => askDelete(t.id)} aria-label={`Apagar ${t.title ?? 'conversa'}`} title="Apagar"><Trash2 /></button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="conv">
        {agent && (
          <div className="agent-crumb">
            <button type="button" className="agent-back" onClick={() => nav('/agentes')} aria-label="Voltar para Agentes"><ArrowLeft /></button>
            <AgentAvatar icon={agent.icon} color={agent.color} size={14} photo={agent.photo} name={agent.name} />
            <span>{agent.name}</span>
          </div>
        )}
        <div className="conv-h">
          {id && editing?.id === id && editing.where === 'head' ? (
            <input className="head-input" autoFocus maxLength={80} value={draft} aria-label="Novo nome da conversa" placeholder="Nome da conversa"
              onChange={(e) => setDraft(e.target.value)} onKeyDown={renameKeys} onBlur={() => void commitRename()} onFocus={(e) => e.currentTarget.select()} />
          ) : (
            <h1 title={thread?.title ?? ''}>{thread?.title ?? (id ? 'Nova conversa' : 'Conversas')}</h1>
          )}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <ModelPicker me={me} value={model} onChange={setModel} scope={scopeRow} models={sm} />
            {id && (
              <>
                <button className="btn sm ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => startRename(id, thread?.title ?? threads?.find((t) => t.id === id)?.title ?? null, 'head')}><Pencil />renomear</button>
                {confirming === id ? (
                  <span className="head-confirm"><span>apagar esta conversa?</span><small className="head-warn">{delWarn}</small>
                    <button className="btn sm danger" onClick={() => void delThread(id)}>sim, apagar</button>
                    <button className="btn sm ghost" onClick={() => setConfirming(null)}>não</button></span>
                ) : <button className="btn sm ghost danger" onClick={() => askDelete(id)}><Trash2 />apagar</button>}
              </>
            )}
          </div>
        </div>
        
        {me.org?.notice && <div className="org-notice" role="note">Esta conta pertence à empresa {me.org.name}: o administrador pode ver estas conversas.</div>}
        <div className="msgs" ref={msgsRef}>
          {bubbles.length === 0 && (
            <div className="welcome">
              <h2>{id ? 'Nova conversa iniciada' : agent ? `Converse com ${agent.name}` : 'O que você quer processar?'}</h2>
              <p className="mute" style={{ margin: 0, fontSize: 14 }}>
                {id
                  ? 'Envie uma mensagem ou anexe uma imagem abaixo.'
                  : agent
                    ? (agent.description || 'Escreva uma mensagem para começar.')
                    : 'Escreva uma pergunta ou anexe um arquivo. Cada interação mostra o custo de tokens em tempo real.'}
              </p>
            </div>
          )}
          {bubbles.map((b) => <Row key={b.id} b={b} onStop={onStop} onStopJob={onStopJob} onOpenSource={openSource} />)}
        </div>
        
        <div className="composer">
          <div style={{ width: '100%', maxWidth: '800px', padding: '0 24px' }}>
            <ChatQueue queue={view.queue} paused={view.phase === 'paused'} onRemove={onRemove} onSendNow={onSendNow} onResume={onResume} onClear={onClear} />
            <div className="sr-only" role="status" aria-live="polite">{announce}</div>
            {pending.length > 0 && (
              <div className="previews">
                {pending.map((b, i) => (
                  <div key={i} className="th">
                    <img src={`data:image/jpeg;base64,${b}`} alt="" />
                    <button type="button" onClick={() => setPending((cur) => cur.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
              </div>
            )}
            {picked.length > 0 && (
              <div className="chat-docs" role="list" aria-label="Documentos desta conversa">
                {picked.map((d) => (
                  <span key={d.id} role="listitem" className="dchip" title={d.name}>
                    <FileText /><span className="dchip-name">{d.name}</span>
                    <button type="button" aria-label={`Remover ${d.name}`} onClick={() => setPicked((cur) => cur.filter((x) => x.id !== d.id))}><X /></button>
                  </span>
                ))}
                {needsNotice && me.prefs?.docsNoticeAck && <span className="docs-hint">trechos vão a máquinas de terceiros — evite dados sensíveis</span>}
              </div>
            )}
            {notice && (
              <div className="docs-notice" role="alertdialog" aria-labelledby="docs-notice-t" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setNotice(false); inputRef.current?.focus(); } }}>
                <p id="docs-notice-t">{NOTICE}</p>
                <div className="docs-notice-acts">
                  <button type="button" className="btn sm primary" autoFocus onClick={() => void confirmNotice()}>Entendi, continuar</button>
                  <button type="button" className="btn sm ghost" onClick={() => { setNotice(false); inputRef.current?.focus(); }}>Cancelar</button>
                </div>
              </div>
            )}
            <div className="input-box">
              <div className="input-tools">
                <button 
                  className="btn iconbtn" 
                  style={{ background: 'transparent', border: 'none', color: 'var(--mute)', padding: '4px', height: 'auto' }} 
                  title={me.visionModels.length ? 'Anexar imagem ou documento (ou cole / arraste)' : 'Anexar documento (nenhum modelo com visão disponível agora para imagens)'} 
                  aria-label="Anexar imagem ou documento"
                  onClick={() => fileRef.current?.click()}
                >
                  <IconClip />
                </button>
                <DocPicker selected={picked} onChange={setPicked} max={MAX_DOCS} />
                <input type="file" ref={fileRef} accept={`${me.visionModels.length ? 'image/*,' : ''}${DOC_ACCEPT}`} multiple hidden onChange={async (e) => { for (const f of e.target.files ?? []) await attach(f); e.target.value = ''; }} />
                <textarea 
                  ref={inputRef} 
                  rows={1} 
                  placeholder="Mensagem para a malha de inferência..." 
                  value={input} 
                  onChange={(e) => { setInput(e.target.value); autoGrow(); }} 
                  onKeyDown={onKey} 
                  onPaste={onPaste} 
                />
              </div>
              <button className="btn primary submit-btn" title={streaming ? 'Adicionar à fila' : 'Enviar'} aria-label={streaming ? 'Adicionar à fila' : 'Enviar'} onClick={() => submit()}>
                <IconSend />
              </button>
            </div>
            <div className="mono mute" style={{ fontSize: 11, marginTop: 12, textAlign: 'center' }}>
              {rateLine()}
            </div>
          </div>
        </div>
      </div>
      {viewer && <DocViewer target={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

/** Uma bolha. Memoizada: no streaming só a que recebe tokens muda de objeto (`patchBubble`), as outras não re-renderizam. */
const Row = memo(function Row({ b, onStop, onStopJob, onOpenSource }: { b: Bubble; onStop: () => void; onStopJob: (jobId: string) => void; onOpenSource: (s: Source, all: Source[]) => void }) {
  // Copiar só em resposta concluída (a do histórico também): nunca em erro, nem enquanto a resposta ainda está sendo escrita
  const canCopy = b.role === 'assistant' && !b.err && !b.cursor && !!b.text && (!b.meta || b.meta.kind === 'done');
  return (
    <div className={`msg ${b.role}`}>
      <div className={`bubble${b.err ? ' err' : ''}`}>
        {b.images.map((src, j) => <img key={j} src={src} alt="" style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 8 }} />)}
        {b.role === 'assistant' && !b.err ? <MessageBody text={b.text} cursor={b.cursor} /> : b.text}
      </div>
      {b.sources && b.sources.length > 0 && (
        <div className="sources"><span className="sources-l">Fontes:</span>
          {b.sources.map((s, j) => <button type="button" key={j} className="src-chip btn-chip" title={`${s.section ? `${s.name} › ${s.section}` : s.name} — clique para pré-visualizar`} onClick={() => onOpenSource(s, b.sources ?? [])}><FileText /><span className="src-name">{s.name}</span><span className="src-n"> · {s.seq != null ? `trecho ${s.seq + 1}` : `${s.chunks} ${s.chunks === 1 ? 'trecho' : 'trechos'}`}</span></button>)}
        </div>
      )}
      {b.note && <div className="sources-note">{b.note}</div>}
      <div className="meta">
        {b.meta && <MetaLine meta={b.meta} onStop={onStop} onStopJob={onStopJob} />}
        {b.interrupted && <span className="pill">interrompida</span>}
        {canCopy && <CopyButton text={b.text} label="Copiar resposta" />}
      </div>
    </div>
  );
});

function MetaLine({ meta, onStop, onStopJob }: { meta: Meta; onStop: () => void; onStopJob: (jobId: string) => void }) {
  switch (meta.kind) {
    case 'text': return <>{meta.text === 'enviando' && <span className="dot" />} {meta.text}</>;
    case 'queued': return <><span className="dot" /> na fila · posição {meta.position}</>;
    case 'started': return <><span className="dot fil" /> {meta.node} <span className="ticker">{meta.tokens} tok · {brl(meta.cost)} · {meta.secs.toFixed(1)}s</span> <button className="btn sm ghost danger" onClick={() => (meta.attached && meta.jobId ? onStopJob(meta.jobId) : onStop())}>parar</button></>;
    case 'done': return <>{meta.text}</>;
  }
}
